import firebase from "./firebase";
import "firebase/compat/auth";
import { axiosGraphQL as defaultAxiosGraphQL } from "../api";
import {
  CONFIRM_MOBILE_CHANGE,
  GET_USER,
  PREPARE_MOBILE_CHANGE,
} from "../graphql/userGraphql";
import { getStorage } from "./utils";
import {
  isExplicitlyImpersonating,
  refreshAuthoritativeUser,
} from "./emailChange";
import { persistFirebaseToken } from "./firebaseSession";

export const PENDING_MOBILE_CHANGE_KEY = "pendingMobileChange";
const E164_PATTERN = /^\+[1-9]\d{7,14}$/;

export const normalizeMobile = (mobile) =>
  typeof mobile === "string" ? mobile.replace(/[\s().-]/g, "") : "";

export const isValidE164Mobile = (mobile) =>
  E164_PATTERN.test(normalizeMobile(mobile));

const graphQlData = (response, operation) => {
  if (response.data?.errors?.length) {
    throw new Error(response.data.errors[0]?.message || "mobile_change_failed");
  }
  return response.data?.data?.[operation];
};

export const getPendingMobileChange = () => {
  const value = getStorage().getItem(PENDING_MOBILE_CHANGE_KEY);
  if (!value) return null;
  try {
    const pending = JSON.parse(value);
    if (
      pending?.firebaseUid &&
      isValidE164Mobile(pending.mobile) &&
      pending.firebaseUpdated === true
    ) {
      return pending;
    }
  } catch (_) {
    // Invalid retry state is cleared below.
  }
  getStorage().removeItem(PENDING_MOBILE_CHANGE_KEY);
  return null;
};

export const clearPendingMobileChange = () =>
  getStorage().removeItem(PENDING_MOBILE_CHANGE_KEY);

const savePendingMobileChange = ({ mobile, firebaseUid }) =>
  getStorage().setItem(
    PENDING_MOBILE_CHANGE_KEY,
    JSON.stringify({ mobile, firebaseUid, firebaseUpdated: true })
  );

const requireSelfServiceUser = () => {
  if (isExplicitlyImpersonating()) {
    throw new Error("mobile_change_impersonation_forbidden");
  }
  const currentUser = firebase.auth().currentUser;
  if (!currentUser?.uid) throw new Error("mobile_change_auth_required");
  return currentUser;
};

export const initiateMobileChange = async ({
  mobile,
  createVerifier,
  axiosGraphQL = defaultAxiosGraphQL,
}) => {
  const currentUser = requireSelfServiceUser();
  const normalizedMobile = normalizeMobile(mobile);
  if (!isValidE164Mobile(normalizedMobile)) {
    throw new Error("invalid_mobile");
  }

  const prepared = await axiosGraphQL({
    query: PREPARE_MOBILE_CHANGE,
    variables: { mobile: normalizedMobile },
  });
  if (graphQlData(prepared, "prepareMobileChange") !== true) {
    throw new Error("mobile_change_preflight_failed");
  }

  const verifier = createVerifier();
  const provider = new firebase.auth.PhoneAuthProvider();
  const verificationId = await provider.verifyPhoneNumber(
    normalizedMobile,
    verifier
  );
  return {
    firebaseUid: currentUser.uid,
    mobile: normalizedMobile,
    verificationId,
  };
};

const synchronizeMobile = async ({
  mobile,
  firebaseUid,
  previousUser,
  axiosGraphQL,
}) => {
  const currentUser = requireSelfServiceUser();
  if (currentUser.uid !== firebaseUid) {
    clearPendingMobileChange();
    throw new Error("mobile_change_account_mismatch");
  }

  const token = await persistFirebaseToken(currentUser);
  const confirmation = await axiosGraphQL({ query: CONFIRM_MOBILE_CHANGE });
  graphQlData(confirmation, "confirmMobileChange");
  const user = await refreshAuthoritativeUser({ axiosGraphQL, previousUser });
  if (normalizeMobile(user.mobile) !== mobile) {
    throw new Error("mobile_change_sync_pending");
  }
  clearPendingMobileChange();
  return { token, user };
};

export const confirmMobileVerification = async ({
  verificationId,
  code,
  mobile,
  firebaseUid,
  previousUser,
  axiosGraphQL = defaultAxiosGraphQL,
}) => {
  const currentUser = requireSelfServiceUser();
  if (currentUser.uid !== firebaseUid) {
    throw new Error("mobile_change_account_mismatch");
  }
  const credential = firebase.auth.PhoneAuthProvider.credential(
    verificationId,
    code
  );
  await currentUser.updatePhoneNumber(credential);
  savePendingMobileChange({ mobile, firebaseUid });
  return synchronizeMobile({
    mobile,
    firebaseUid,
    previousUser,
    axiosGraphQL,
  });
};

export const retryMobileSynchronization = async ({
  previousUser,
  axiosGraphQL = defaultAxiosGraphQL,
}) => {
  const pending = getPendingMobileChange();
  if (!pending) throw new Error("mobile_change_retry_unavailable");
  return synchronizeMobile({
    ...pending,
    previousUser,
    axiosGraphQL,
  });
};
